import { BoxIcon, BracesIcon, WrenchIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

import { PromptEntityCard, ToolCallCard } from "./EntityCards";
import { detectIoFormat, IoFormatSelect, IoValueBody } from "./IoValueViewer";
import type { IoFormat } from "./IoValueViewer";
import { collectSubtreeToolCalls, getSpanPromptEntity, toKeyValueRows } from "./utils";

import { cn } from "@/lib/utils";
import type { TraceEntity } from "@/queries";
import type { SpanNode } from "@/types/spans";

const SECTION_IDS = ["overview", "input", "output", "entities", "tools", "json"] as const;
type SectionId = (typeof SECTION_IDS)[number];

const TAB_LABELS: Record<SectionId, string> = {
  overview: "Overview",
  input: "Input",
  output: "Output",
  entities: "Entities",
  tools: "Tools",
  json: "JSON",
};

const HIGHLIGHT_DURATION_MS = 1600;
const STICKY_BAR_HEIGHT_PX = 48;

const getScrollParent = (element: HTMLElement): HTMLElement | null => {
  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    const { overflowY } = window.getComputedStyle(parent);
    if (overflowY === "auto" || overflowY === "scroll") {
      return parent;
    }
  }
  return null;
};

const isSectionOnScreen = (element: HTMLElement): boolean => {
  const rect = element.getBoundingClientRect();
  const parent = getScrollParent(element);
  const bounds = parent ? parent.getBoundingClientRect() : { top: 0, bottom: window.innerHeight };
  return rect.bottom > bounds.top + STICKY_BAR_HEIGHT_PX && rect.top < bounds.bottom;
};

const Section = ({
  title,
  description,
  icon,
  action,
  rightAction,
  children,
}: {
  title: string;
  description?: string;
  icon?: ReactNode;
  action?: ReactNode;
  rightAction?: ReactNode;
  children: ReactNode;
}) => (
  <section className="border-t border-gray-200 pt-5 first:border-t-0 first:pt-0">
    <div className="mb-2 flex items-start justify-between gap-2">
      <div className="min-w-0">
        <h4 className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
          {icon}
          {title}
          {action}
        </h4>
        {description ? <p className="mt-1 text-xs text-gray-500">{description}</p> : null}
      </div>
      {rightAction ? <div className="shrink-0">{rightAction}</div> : null}
    </div>
    {children}
  </section>
);

const JumpToSpanLink = ({
  spanId,
  onJumpToSpan,
}: {
  spanId?: string | null;
  onJumpToSpan?: (spanId: string) => void;
}) =>
  spanId && onJumpToSpan ? (
    <button
      type="button"
      onClick={() => onJumpToSpan(spanId)}
      title="Open the span that produced this"
      className="translate-y-px text-[11px] font-medium text-blue-600 transition-colors hover:text-blue-700"
    >
      View span
    </button>
  ) : null;

const AnchorSection = ({
  sectionId,
  highlighted,
  registerRef,
  children,
}: {
  sectionId: SectionId;
  highlighted: boolean;
  registerRef: (sectionId: SectionId, element: HTMLDivElement | null) => void;
  children: ReactNode;
}) => (
  <div
    ref={element => registerRef(sectionId, element)}
    className={cn(
      "-m-2 scroll-mt-14 rounded-lg p-2 transition-colors duration-700",
      highlighted && "bg-blue-50/70",
    )}
  >
    {children}
  </div>
);

const EmptyNote = ({ children }: { children: ReactNode }) => (
  <p className="rounded-lg border border-gray-100 bg-gray-50/50 px-3 py-4 text-center text-sm text-gray-500">
    {children}
  </p>
);

interface SpanTabsProps {
  span: SpanNode;
  header: ReactNode;
  input: unknown;
  output: unknown;
  outputTitle?: string;
  traceEntities?: TraceEntity[];
  inputVariableChips?: boolean;
  inputSourceSpanId?: string | null;
  outputSourceSpanId?: string | null;
  rawData: ReactNode;
  onJumpToSpan?: (spanId: string) => void;
}

const SpanTabs = ({
  span,
  header,
  input,
  output,
  outputTitle = "Output",
  traceEntities = [],
  inputVariableChips = false,
  inputSourceSpanId,
  outputSourceSpanId,
  rawData,
  onJumpToSpan,
}: SpanTabsProps) => {
  const [activeTab, setActiveTab] = useState<SectionId>("overview");
  const [highlightedSection, setHighlightedSection] = useState<SectionId | null>(null);
  // null = follow the detected format; a value = explicit user override for this span
  const [inputFormat, setInputFormat] = useState<IoFormat | null>(null);
  const [outputFormat, setOutputFormat] = useState<IoFormat | null>(null);
  const sectionRefs = useRef<Partial<Record<SectionId, HTMLDivElement | null>>>({});
  const highlightTimerRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (highlightTimerRef.current !== null) {
        window.clearTimeout(highlightTimerRef.current);
      }
    },
    [],
  );

  // Reset format overrides when the panel switches to a different span
  useEffect(() => {
    setInputFormat(null);
    setOutputFormat(null);
  }, [span.span_id]);

  const inputRows = toKeyValueRows(input);
  const outputRows = toKeyValueRows(output);
  const resolvedInputFormat = inputFormat ?? detectIoFormat(input);
  const resolvedOutputFormat = outputFormat ?? detectIoFormat(output);
  const toolCalls = collectSubtreeToolCalls(span);

  // Trace-level entities (from the trace's request logs) + this span's own attribute-declared prompt
  const attributeEntity = getSpanPromptEntity(span);
  const promptEntities = [
    ...traceEntities.map(entity => ({
      kind: "prompt" as const,
      name: entity.name,
      version:
        entity.version === null || entity.version === undefined
          ? undefined
          : String(entity.version),
      registryId: String(entity.id),
    })),
    ...(attributeEntity && !traceEntities.some(entity => entity.name === attributeEntity.name)
      ? [attributeEntity]
      : []),
  ];

  const registerSectionRef = useCallback((sectionId: SectionId, element: HTMLDivElement | null) => {
    sectionRefs.current[sectionId] = element;
  }, []);

  const handleTabClick = (sectionId: SectionId) => {
    setActiveTab(sectionId);
    const section = sectionRefs.current[sectionId];
    if (section && !isSectionOnScreen(section)) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    if (sectionId !== "overview") {
      setHighlightedSection(sectionId);
      if (highlightTimerRef.current !== null) {
        window.clearTimeout(highlightTimerRef.current);
      }
      highlightTimerRef.current = window.setTimeout(
        () => setHighlightedSection(null),
        HIGHLIGHT_DURATION_MS,
      );
    }
  };

  return (
    <div className="w-full">
      <div className="sticky top-0 z-10 mb-4 flex gap-1 border-b border-gray-200 bg-white">
        {SECTION_IDS.map(sectionId => (
          <button
            key={sectionId}
            type="button"
            onClick={() => handleTabClick(sectionId)}
            className={cn(
              "h-10 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors",
              activeTab === sectionId
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-gray-600 hover:text-gray-800",
            )}
          >
            {TAB_LABELS[sectionId]}
          </button>
        ))}
      </div>
      <div className="flex flex-col gap-y-5">
        <AnchorSection sectionId="overview" highlighted={false} registerRef={registerSectionRef}>
          {header}
        </AnchorSection>
        <AnchorSection
          sectionId="input"
          highlighted={highlightedSection === "input"}
          registerRef={registerSectionRef}
        >
          <Section
            title="Input"
            description="Values passed into this span"
            icon={<BracesIcon className="h-4 w-4 text-gray-400" />}
            action={<JumpToSpanLink spanId={inputSourceSpanId} onJumpToSpan={onJumpToSpan} />}
            rightAction={
              inputRows.length ? (
                <IoFormatSelect value={resolvedInputFormat} onChange={setInputFormat} />
              ) : undefined
            }
          >
            {inputRows.length ? (
              <IoValueBody
                value={input}
                format={resolvedInputFormat}
                variableChips={inputVariableChips}
              />
            ) : (
              <EmptyNote>No input recorded for this span.</EmptyNote>
            )}
          </Section>
        </AnchorSection>
        <AnchorSection
          sectionId="output"
          highlighted={highlightedSection === "output"}
          registerRef={registerSectionRef}
        >
          <Section
            title={outputTitle}
            description="The value returned by this span"
            icon={<BracesIcon className="h-4 w-4 text-gray-400" />}
            action={<JumpToSpanLink spanId={outputSourceSpanId} onJumpToSpan={onJumpToSpan} />}
            rightAction={
              outputRows.length ? (
                <IoFormatSelect value={resolvedOutputFormat} onChange={setOutputFormat} />
              ) : undefined
            }
          >
            {outputRows.length ? (
              <IoValueBody value={output} format={resolvedOutputFormat} />
            ) : (
              <EmptyNote>No output recorded for this span.</EmptyNote>
            )}
          </Section>
        </AnchorSection>
        <AnchorSection
          sectionId="entities"
          highlighted={highlightedSection === "entities"}
          registerRef={registerSectionRef}
        >
          <Section
            title="Entities used"
            description="PromptLayer resources referenced by this span"
            icon={<BoxIcon className="h-4 w-4 text-gray-400" />}
          >
            {promptEntities.length ? (
              <div className="flex flex-col gap-2">
                {promptEntities.map(entity => (
                  <PromptEntityCard key={entity.registryId ?? entity.name} entity={entity} />
                ))}
              </div>
            ) : (
              <EmptyNote>No PromptLayer entities were linked to this span.</EmptyNote>
            )}
          </Section>
        </AnchorSection>
        <AnchorSection
          sectionId="tools"
          highlighted={highlightedSection === "tools"}
          registerRef={registerSectionRef}
        >
          <Section
            title="Tools used"
            description="Tools called by this span and its children"
            icon={<WrenchIcon className="h-4 w-4 text-gray-400" />}
          >
            {toolCalls.length ? (
              <div className="flex flex-col gap-2">
                {toolCalls.map(call => (
                  <ToolCallCard key={call.spanId} call={call} onJumpToSpan={onJumpToSpan} />
                ))}
              </div>
            ) : (
              <EmptyNote>No tool calls were recorded for this span.</EmptyNote>
            )}
          </Section>
        </AnchorSection>
        <AnchorSection
          sectionId="json"
          highlighted={highlightedSection === "json"}
          registerRef={registerSectionRef}
        >
          <Section
            title="Raw span data"
            description="Full JSON captured for debugging and filtering"
            icon={<BracesIcon className="h-4 w-4 text-gray-400" />}
          >
            {rawData}
          </Section>
        </AnchorSection>
      </div>
    </div>
  );
};

export default SpanTabs;
